/**
 * Where a material comes from, the same way wherever one is added or changed:
 * a session's new reading, a reading's row in Prepare, the material editor and
 * the reading editor.
 *
 * Two sources, read from the tenant's Picker configuration: a file chosen from
 * Google Drive when this site has a Picker, and a link always. The link field
 * never hides, so every way the chooser can come back empty (refused, blocked,
 * not signed in, Google not answering, a file IMAM cannot use) leaves the
 * person a way to finish, with their draft as they left it. A pick reaches the
 * caller already mapped by `selectionToMaterialPayload`, the one mapping.
 *
 * Editors only. A learner never sees this, so the Drive file's Manage access
 * link here is never offered to one.
 */
import { useEffect, useId, useRef, type InputHTMLAttributes, type ReactNode } from "react";
import { HardDrive } from "lucide-react";

import { a3Copy } from "@/components/assignments/a3-copy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ManageDriveAccess } from "./DriveReference";
import { driveKindLine, selectionToMaterialPayload, type MaterialSourcePayload } from "./material-facts";
import { useDriveChooser } from "./use-drive-chooser";

type LinkInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "value" | "onChange" | "disabled">;

export function SourcePicker({
  courseId,
  active = true,
  title,
  link,
  driveMimeType = null,
  onLinkChange,
  onPick,
  disabled = false,
  autoFocus = false,
  linkMessage,
  linkInput,
}: {
  courseId: string;
  /** Whether this editor is live; an inactive one opens nothing and reads no course folder. */
  active?: boolean;
  /** The title typed so far: a pick keeps it, or takes the file's name when there is none. */
  title: string;
  link: string;
  /** The type of the Drive file the link is; absent for a pasted link. */
  driveMimeType?: string | null;
  onLinkChange: (link: string) => void;
  onPick: (payload: MaterialSourcePayload) => void;
  disabled?: boolean;
  /** Focus the first source on mount: Drive when this site has it, else the link. */
  autoFocus?: boolean;
  /** The link field's own problem, already worded and identified by `linkInput`'s describedby. */
  linkMessage?: ReactNode;
  /** The caller's commit behaviour for the link field (blur, Enter, Escape, invalid state). */
  linkInput?: LinkInputProps;
}) {
  const chooser = useDriveChooser(courseId, active);
  const id = useId();
  const linkRef = useRef<HTMLInputElement | null>(null);
  const { triggerRef } = chooser;
  const driveReady = chooser.enabled;
  // The editor stays open while Google is: a pick maps the title and lands in
  // the caller as they are when the file comes back, not as they were at the press.
  const latest = useRef({ title, onPick });
  useEffect(() => { latest.current = { title, onPick }; });

  // The editor opens on its first source. Both inputs are fixed for the life of
  // the page (the host's Picker configuration is read once), so this runs once.
  useEffect(() => {
    if (autoFocus) (driveReady ? triggerRef.current : linkRef.current)?.focus();
  }, [autoFocus, driveReady, triggerRef]);

  const choose = async () => {
    const selection = await chooser.choose();
    if (selection) latest.current.onPick(selectionToMaterialPayload(selection, latest.current.title));
  };

  const reason = `${id}-drive-reason`;
  return (
    <fieldset className="grid min-w-0 gap-2" data-source-picker="">
      <legend className="mb-1.5 text-ui font-medium text-foreground">{a3Copy.source}</legend>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          ref={triggerRef}
          type="button"
          size="sm"
          variant="outline"
          disabled={!driveReady || chooser.busy || disabled}
          aria-describedby={driveReady ? undefined : reason}
          onClick={() => void choose()}
        >
          <HardDrive className="size-4" strokeWidth={1.75} aria-hidden />
          {chooser.busy ? a3Copy.openingGoogleDrive : a3Copy.chooseFromGoogleDrive}
        </Button>
        {chooser.busy && (
          <Button type="button" size="sm" variant="ghost" onClick={chooser.cancel}>{a3Copy.cancelOpeningGoogleDrive}</Button>
        )}
      </div>
      {!driveReady && <p id={reason} className="text-meta text-muted-foreground">{a3Copy.driveSelectionUnavailableOnThisSite}</p>}
      {/* ADR 0025. Names the second tab before the chooser opens; absent, it
          says there is no course folder. */}
      {driveReady && chooser.folderReady && (
        <p className="text-meta text-muted-foreground">{a3Copy.googleDriveOpensInYourOwnDriveThisCourse}</p>
      )}
      {chooser.error && <p role="alert" className="text-meta text-destructive">{chooser.error}</p>}
      <div className="grid min-w-0 gap-1.5">
        <Label htmlFor={`${id}-link`}>{a3Copy.link}</Label>
        <Input
          ref={linkRef}
          id={`${id}-link`}
          value={link}
          placeholder={a3Copy.https2}
          disabled={disabled}
          onChange={(event) => onLinkChange(event.target.value)}
          {...linkInput}
        />
        {linkMessage}
      </div>
      {driveMimeType && (
        <div className="grid justify-items-start">
          <p className="text-meta text-muted-foreground">{driveKindLine(driveMimeType)}. {a3Copy.googleDecidesWhoCanOpenThisFile}</p>
          <ManageDriveAccess url={link} />
        </div>
      )}
    </fieldset>
  );
}
